import { IGatsbySvg } from "@bond-london/gatsby-transformer-extracted-svg";
import { IRichTextInformation } from "@bond-london/graphcms-rich-text";
import classNames from "classnames";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import React from "react";
import { Trans } from "react-i18next";
import { RTF } from "../elements/RTF";
import { SimpleLink } from "../elements/SimpleLink";
import { useEntryAnimation } from "../hooks/Visibility";
import { lookupColourString } from "../lookups";
import {
  MainWideTextClassName,
  P4RTFOverrides,
  SlideHeightClassName,
  SlidePaddingClassName,
} from "../styles";

export const Slide: React.FC<{
  id: string;
  title?: string | null;
  alt?: string | null;
  headline?: string | null;
  message?: IRichTextInformation | null;
  shape?: IGatsbySvg | null;
  image?: IGatsbyImageData | null;
  imageShape?: IGatsbySvg | null;
  background: string;
  link?: string | null;
  linkTitle?: string | null;
}> = ({
  id,
  title,
  headline,
  message,
  shape,
  image,
  imageShape,
  alt,
  background,
  link,
  linkTitle,
}) => {
  const [sectionRef, animationMode] = useEntryAnimation();
  return (
    <section
      ref={sectionRef}
      data-component="Normal Slide"
      id={id}
      className={classNames(
        "relative",
        lookupColourString(background, "bg"),
        lookupColourString(background, "textForBg"),
        SlidePaddingClassName,
        SlideHeightClassName,
      )}
    >
      <div className={classNames("page-grid max-width relative h-full")}>
        {shape && (
          <div
            className={classNames(
              animationMode,
              "animate-appear",
              "absolute flex items-center justify-end",
              "bottom-mobile-margin left-mobile-margin right-mobile-margin top-mobile-margin",
              "md:bottom-tablet-margin md:left-tablet-margin md:right-tablet-margin md:top-tablet-margin",
              "lg:bottom-desktop-margin lg:left-desktop-margin lg:right-desktop-margin lg:top-desktop-margin",
            )}
          >
            <img
              src={shape.encoded ?? shape.encodedUrl}
              className="h-full object-contain"
              alt=""
            />
          </div>
        )}
        {(image ?? imageShape) && (
          <div
            className={classNames(
              "col-span-full col-start-1",
              "md:col-span-4 md:col-start-6",
              "lg:col-span-7 lg:col-start-9",
              "row-start-1",
              "md:row-span-3 md:row-start-1",
              animationMode,
              "animate-enter-from-top",
              "self-center",
            )}
          >
            <div className="aspect-h-5 aspect-w-7">
              {image && (
                <GatsbyImage
                  style={{ position: "absolute" }}
                  image={image}
                  alt={alt ?? title ?? "Image"}
                  loading="lazy"
                />
              )}
              {imageShape && (
                <img
                  className="absolute"
                  alt={alt ?? title ?? "Image"}
                  src={imageShape.encoded ?? imageShape.encodedUrl}
                />
              )}
            </div>
          </div>
        )}
        <div
          className={classNames(
            "relative mt-48px md:mt-0",
            "col-span-4 col-start-2 md:col-span-3 md:col-start-2 lg:col-span-5 lg:col-start-3",
            "md:row-span-3 md:row-start-1",
            animationMode,
            "self-center",
            "animate-enter-from-bottom",
          )}
        >
          {headline && <p className={classNames("p3")}>{headline}</p>}
          {title && <h1 className={classNames("h1 mt-20px")}>{title}</h1>}
          {message && (
            <RTF
              className={classNames("mt-20px md:mt-48px")}
              classNameOverrides={P4RTFOverrides}
              content={message}
            />
          )}
          {link && (
            <div
              className={classNames(
                MainWideTextClassName,
                "flex",
                "mt-30px lg:mt-60px",
                animationMode,
                "animate-enter-from-bottom",
              )}
            >
              <SimpleLink to={link}>
                {linkTitle ?? <Trans>Learn more</Trans>}
              </SimpleLink>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
